import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { onAnimationEnd } from '@theme/utilities';

/**
 * A custom element that manages the main menu drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDetailsElement} details - The details element.
 *
 * @extends {Component<Refs>}
 */
class HeaderDrawer extends Component {
  requiredRefs = ['details'];

  // ===== Swipe-to-close (mobile) =====
  #mobileMQ = window.matchMedia('(max-width: 749px)');

  #touchStartX = 0;
  #touchStartY = 0;
  #lastX = 0;

  #tracking = false;
  #dragging = false;

  #panel = null;
  #panelWidth = 0;

  #thresholdPx = 70; // swipe distance to close
  #angleRatio = 1.2; // horizontal must dominate vertical
  #startDx = -10; // start drag when moving left at least 10px

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('keyup', this.#onKeyUp);
    this.#setupAnimatedElementListeners();

    // Swipe-to-close
    this.addEventListener('touchstart', this.#onTouchStart, { passive: true });
    this.addEventListener('touchmove', this.#onTouchMove, { passive: false });
    this.addEventListener('touchend', this.#onTouchEnd, { passive: true });
    this.addEventListener('touchcancel', this.#onTouchCancel, { passive: true });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keyup', this.#onKeyUp);

    this.removeEventListener('touchstart', this.#onTouchStart);
    this.removeEventListener('touchmove', this.#onTouchMove);
    this.removeEventListener('touchend', this.#onTouchEnd);
    this.removeEventListener('touchcancel', this.#onTouchCancel);
  }

  /**
   * Close the main menu drawer when the Escape key is pressed
   * @param {KeyboardEvent} event
   */
  #onKeyUp = (event) => {
    if (event.key !== 'Escape') return;

    this.#close(this.#getDetailsElement(event));
  };

  /**
   * @returns {boolean} Whether the main menu drawer is open
   */
  get isOpen() {
    return this.refs.details.hasAttribute('open');
  }

  /**
   * Get the closest details element to the event target
   * @param {Event | undefined} event
   * @returns {HTMLDetailsElement}
   */
  #getDetailsElement(event) {
    if (!(event?.target instanceof Element)) return this.refs.details;

    return event.target.closest('details') ?? this.refs.details;
  }

  /**
   * Toggle the main menu drawer
   */
  toggle() {
    return this.isOpen ? this.close() : this.open();
  }

  /**
   * Open the closest drawer or the main menu drawer
   * @param {Event} [event]
   */
  open(event) {
    const details = this.#getDetailsElement(event);
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'true');

    this.preventInitialAccordionAnimations(details);
    requestAnimationFrame(() => {
      details.classList.add('menu-open');
      setTimeout(() => {
        trapFocus(details);
      }, 0);
    });
  }

  /**
   * Go back or close the main menu drawer
   * @param {Event} [event]
   */
  back(event) {
    this.#close(this.#getDetailsElement(event));
  }

  /**
   * Close the main menu drawer
   */
  close() {
    this.#close(this.refs.details);
  }

  /**
   * Close the closest menu or submenu that is open
   *
   * @param {HTMLDetailsElement} details
   */
  #close(details) {
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'false');
    details.classList.remove('menu-open');

    onAnimationEnd(details, () => {
      reset(details);
      if (details === this.refs.details) {
        removeTrapFocus();
        const openDetails = this.querySelectorAll('details[open]:not(accordion-custom > details)');
        openDetails.forEach(reset);
      } else {
        setTimeout(() => {
          trapFocus(this.refs.details);
        }, 0);
      }
    });

    // Ensure any swipe transform is cleared
    this.#resetSwipeStyles();
  }

  /**
   * Attach animationend event listeners to all animated elements to remove will-change after animation
   * to remove the stacking context and allow submenus to be positioned correctly
   */
  #setupAnimatedElementListeners() {
    /**
     * @param {AnimationEvent} event
     */
    function removeWillChangeOnAnimationEnd(event) {
      const target = event.target;
      if (target && target instanceof HTMLElement) {
        target.style.setProperty('will-change', 'unset');
        target.removeEventListener('animationend', removeWillChangeOnAnimationEnd);
      }
    }
    const allAnimated = this.querySelectorAll('.menu-drawer__animated-element');
    allAnimated.forEach((element) => {
      element.addEventListener('animationend', removeWillChangeOnAnimationEnd);
    });
  }

  /**
   * Temporarily disables accordion animations to prevent unwanted transitions when the drawer opens.
   * Adds a no-animation class to accordion content elements, then removes it after 100ms to
   * re-enable animations for user interactions.
   * @param {HTMLDetailsElement} details - The details element containing the accordions
   */
  preventInitialAccordionAnimations(details) {
    const content = details.querySelectorAll('accordion-custom .details-content');

    content.forEach((element) => {
      if (element instanceof HTMLElement) {
        element.classList.add('details-content--no-animation');
      }
    });
    setTimeout(() => {
      content.forEach((element) => {
        if (element instanceof HTMLElement) {
          element.classList.remove('details-content--no-animation');
        }
      });
    }, 100);
  }

  // ===== Swipe-to-close implementation =====

  #getSwipePanel() {
    // The element that visually slides.
    // Adjust if your markup differs; these are common in Shopify themes.
    return (
      this.querySelector('.menu-drawer') ||
      this.querySelector('.menu-drawer__inner-container') ||
      this.querySelector('.menu-drawer__content') ||
      this.querySelector('.menu-drawer__navigation') ||
      this
    );
  }

  #setSwipeDragging(on) {
    if (!this.#panel) return;
    if (on) {
      this.#panel.style.willChange = 'transform';
      this.#panel.style.transition = 'none';
    } else {
      this.#panel.style.willChange = '';
      this.#panel.style.transition = '';
    }
  }

  #applySwipeX(px) {
    if (!this.#panel) return;
    this.#panel.style.transform = `translate3d(${px}px, 0, 0)`;
  }

  #resetSwipeStyles() {
    if (!this.#panel) return;
    this.#panel.style.transform = '';
    this.#panel.style.willChange = '';
    this.#panel.style.transition = '';
  }

  #onTouchStart = (e) => {
    if (!this.#mobileMQ.matches) return;
    if (!this.isOpen) return;
    if (!e.touches || e.touches.length !== 1) return;

    this.#panel = this.#getSwipePanel();
    this.#panelWidth = this.#panel.getBoundingClientRect().width || 0;

    const t = e.touches[0];
    this.#touchStartX = t.clientX;
    this.#touchStartY = t.clientY;
    this.#lastX = t.clientX;

    this.#tracking = true;
    this.#dragging = false;
  };

  #onTouchMove = (e) => {
    if (!this.#tracking) return;
    if (!e.touches || e.touches.length !== 1) return;

    const t = e.touches[0];
    const dx = t.clientX - this.#touchStartX;
    const dy = t.clientY - this.#touchStartY;

    // Determine if gesture is intended to be horizontal
    if (!this.#dragging) {
      const horizontalDominant = Math.abs(dx) > Math.abs(dy) * this.#angleRatio;
      if (!horizontalDominant) return;

      // Drawer comes from left, so to close we drag LEFT (dx negative)
      if (dx > this.#startDx) return; // not left enough yet

      this.#dragging = true;
      this.#setSwipeDragging(true);
    }

    // Prevent page scroll while dragging horizontally
    e.preventDefault();

    // clamp: 0 (open) to -panelWidth (fully left offscreen)
    const clamped = Math.max(-this.#panelWidth, Math.min(0, dx));
    this.#applySwipeX(clamped);

    this.#lastX = t.clientX;
  };

  #onTouchEnd = () => {
    if (!this.#tracking) return;

    const dx = this.#lastX - this.#touchStartX;

    this.#tracking = false;

    if (!this.#dragging) {
      this.#dragging = false;
      return;
    }

    this.#setSwipeDragging(false);

    // Close if dragged left enough
    if (dx <= -this.#thresholdPx) {
      // Reset transform first, then close (so animations/classes behave normally)
      this.#resetSwipeStyles();
      this.close();
    } else {
      // Snap back
      this.#resetSwipeStyles();
    }

    this.#dragging = false;
  };

  #onTouchCancel = () => {
    if (!this.#tracking) return;
    this.#tracking = false;

    if (this.#dragging) {
      this.#setSwipeDragging(false);
      this.#resetSwipeStyles();
    }

    this.#dragging = false;
  };
}

if (!customElements.get('header-drawer')) {
  customElements.define('header-drawer', HeaderDrawer);
}

/**
 * Reset an open details element to its original state
 *
 * @param {HTMLDetailsElement} element
 */
function reset(element) {
  element.classList.remove('menu-open');
  element.removeAttribute('open');
  element.querySelector('summary')?.setAttribute('aria-expanded', 'false');
}
